import { getLocale, getIsProduction, getIsAprimoEnabled } from '../Utils/config-utils';

// Export Aprimo API response interfaces
export interface PersonalisationResponse {
	altText: string;
	code: string;
	ctaLink: string;
	description: string;
	image: string;
	name: string;
}

(function () {
	"use strict";

	// Integration is toggled via the "Aprimo:Enabled" app setting. Do nothing when disabled.
	if (!getIsAprimoEnabled()) {
		return;
	}

	var CONFIG = {
		apiEndpoint: "https://forrit.aprimo.com/ci",

		liveWebsiteUrl: "https://forrit.com/",

		// Enable additional logging for dev
		debug: !getIsProduction(),

		// How often tracking data is sent to Aprimo in ms
		sendRequestInterval: 5000,

		assetImpressionSettings: {
			// Track images that are at least 50% visible
			visibilityThreshold: 0.5,

			minimumVisibleTime: 500,

			blacklist: {
				extensions: [".svg", ".ico", ".gif"],
				// Keywords in src or alt text to ignore
				keywords: ["logo", "icon", "favicon", "social", "share", "facebook", "twitter", "instagram", "youtube", "linkedin"],
				// CSS classes to ignore
				classes: ["logo", "icon", "social", "share", "favicon"],
			},
		},
	};

	var aprimoInitialized = false;
	var trackedAssets = new Set();
	var impressionObserver: IntersectionObserver;
	var trackedArticleCodes = new Set<string>();

	var APRIMO_SELECTORS = {
		articleMeta: 'meta[name="aprimo-article-code"]',
		articleBodyAttr: 'data-aprimo-article-code',
		conversionForm: 'form[data-aprimo-conversion]'
	};

	function log(message: string, data?: object) {
		if (CONFIG.debug && window.console) {
			if (data) {
				console.log("[Aprimo] " + message, data);
			} else {
				console.log("[Aprimo] " + message);
			}
		}
	}

	function handleError(error: any, context: any) {
		log("Error in " + context + ": " + error.message);
		if (CONFIG.debug && window.console) {
			console.error("[Aprimo Error]", error);
		}
	}

	function isAprimoAvailable() {
		return typeof (globalThis as any).Aprimo !== "undefined" && (globalThis as any).Aprimo && (globalThis as any).Aprimo.core && (globalThis as any).Aprimo.tracking;
	}

	function initializeAprimo() {
		if (aprimoInitialized) {
			return Promise.resolve();
		}

		return new Promise<void>(function (resolve, reject) {
			if (isAprimoAvailable()) {
				try {
					(globalThis as any).Aprimo.core.init({
						requestDetails: {
							// Default to live for debug since localhost isn't accepted by API due to URL regex validation
							url: CONFIG.debug ? CONFIG.liveWebsiteUrl : window.location.href,
							language: getLocale(),
							device: getDeviceType(),
							operatingSystem: getOperatingSystem(),
							referrer: document.referrer,
							responseControls: {
								includeMissions: true,
							},
							sendRequestInterval: CONFIG.sendRequestInterval,
						},
						api: CONFIG.apiEndpoint,
						debug: CONFIG.debug,
					});

					aprimoInitialized = true;

					log("Aprimo SDK initialized successfully");
					(window as any).appInsights?.trackTrace({ message: "Aprimo SDK initialized successfully", severityLevel: 1 });

					resolve();
				} catch (error) {
					handleError(error, "initializeAprimo");
					reject(error);
				}
			} else {
				// Wait for Aprimo to load
				var attempts = 0;
				var maxAttempts = 50; // 5 seconds max wait

				var checkAprimo = setInterval(function () {
					attempts++;

					if (isAprimoAvailable()) {
						clearInterval(checkAprimo);
						initializeAprimo().then(resolve).catch(reject);
					} else if (attempts >= maxAttempts) {
						clearInterval(checkAprimo);
						reject(new Error("Aprimo SDK failed to load within timeout period"));
					}
				}, 100);
			}
		});
	}

	function getDeviceType() {
		var userAgent = navigator.userAgent.toLowerCase();
		if (/mobile|android|iphone|ipad|tablet/.test(userAgent)) {
			return "mobile";
		}
		return "desktop";
	}

	function getOperatingSystem() {
		var userAgent = navigator.userAgent.toLowerCase();
		if (/windows/.test(userAgent)) return "windows";
		if (/mac/.test(userAgent)) return "mac";
		if (/linux/.test(userAgent)) return "linux";
		if (/android/.test(userAgent)) return "android";
		if (/ios|iphone|ipad/.test(userAgent)) return "ios";
		return "unknown";
	}

	function normalizePathToArticleCode(pathname: string) {
		var path = (pathname || "").trim();
		if (!path || path === "/") return "home";
		// remove locale prefix like /en/...
		path = path.replace(/^\/[a-z]{2}(\/|$)/i, "/");
		path = path.replace(/^\//, "");
		path = path.replace(/\/$/, "");
		return path || "home";
	}

	function getArticleCode(): string {
		try {
			var meta = document.querySelector(APRIMO_SELECTORS.articleMeta) as HTMLMetaElement | null;
			var fromMeta = meta?.content?.trim();
			if (fromMeta) return fromMeta;

			var fromBody = document.body?.getAttribute(APRIMO_SELECTORS.articleBodyAttr)?.trim();
			if (fromBody) return fromBody;

			return normalizePathToArticleCode(window.location.pathname);
		} catch {
			return "home";
		}
	}

	function trackArticleView(articleCode: string) {
		if (!isAprimoAvailable()) {
			log("Aprimo not available for article tracking");
			return;
		}

		if (!articleCode) return;
		if (trackedArticleCodes.has(articleCode)) return;
		trackedArticleCodes.add(articleCode);

		var trackingData = {
			actionType: "article",
			details: {
				articleCode: articleCode,
			},
		};

		try {
			if (CONFIG.debug) {
				log("Article view would be recorded (skipped due to Debug mode)", trackingData);
			} else {
				(globalThis as any).Aprimo.tracking.sendTrackingRequest(trackingData);
			}
		} catch (error) {
			handleError(error, "trackArticleView");
		}
	}

	function trackConversion(conversionType: string) {
		if (!isAprimoAvailable()) {
			log("Aprimo not available for conversion tracking");
			return;
		}

		if (!conversionType) return;

		var trackingData = {
			actionType: "conversion",
			details: {
				conversionType: conversionType,
			},
		};

		try {
			if (CONFIG.debug) {
				log("Conversion would be recorded (skipped due to Debug mode)", trackingData);
			} else {
				(globalThis as any).Aprimo.tracking.sendTrackingRequest(trackingData);
			}
		} catch (error) {
			handleError(error, "trackConversion");
		}
	}

	function setupConversionTracking() {
		try {
			// Use event delegation to handle HubSpot forms injected after load.
			document.addEventListener(
				"submit",
				function (e) {
					var target = e.target as HTMLElement | null;
					var form = (target && target.closest && target.closest(APRIMO_SELECTORS.conversionForm)) as HTMLFormElement | null;
					if (!form) return;

					var conversionType = form.getAttribute("data-aprimo-conversion")?.trim();
					if (!conversionType) return;

					trackConversion(conversionType);
				},
				true
			);
		} catch (error) {
			handleError(error, "setupConversionTracking");
		}
	}

	function trackAssetImpression(assetElement: any) {
		if (!isAprimoAvailable()) {
			log("Aprimo not available for asset tracking");
			return;
		}

		try {
			var assetUrl = assetElement.src || assetElement.href || "";

			var assetId = assetElement.getAttribute("aprimo-asset-id") || assetElement.id || assetUrl.split("/").pop();

			// Avoid duplicate tracking
			if (trackedAssets.has(assetId)) {
				return;
			}

			trackedAssets.add(assetId);

			var trackingData = {
				actionType: "impression",
				details: {
					assetUrl: assetUrl,
					assetId: assetId,
					slotId: null,
				},
			};

			var slotId = assetElement.getAttribute("aprimo-slot-id") || assetElement.closest("[aprimo-slot-id]")?.getAttribute("aprimo-slot-id");
			if (slotId) {
				trackingData.details.slotId = slotId;
			}

			if (CONFIG.debug) {
				log("Asset impression would be recorded (skipped due to Debug mode)", trackingData);
			} else {
				(globalThis as any).Aprimo.tracking.addTrackingRequest(trackingData);
			}
		} catch (error) {
			handleError(error, "trackAssetImpression");
		}
	}

	function setupAssetImpressionTracking() {
		if (!window.IntersectionObserver) {
			log("IntersectionObserver not supported, falling back to scroll-based tracking");
			setupScrollBasedAssetTracking();
			return;
		}

		try {
			var observerOptions = {
				root: null,
				rootMargin: "0px",
				threshold: CONFIG.assetImpressionSettings.visibilityThreshold,
			};

			impressionObserver = new IntersectionObserver(function (entries) {
				entries.forEach(function (entry) {
					if (entry.isIntersecting) {
						var element = entry.target;

						// Wait for minimum visible time before tracking
						setTimeout(function () {
							if (isElementInViewport(element)) {
								trackAssetImpression(element);
								impressionObserver.unobserve(element);
							}
						}, CONFIG.assetImpressionSettings.minimumVisibleTime);
					}
				});
			}, observerOptions);

			observeAssets();
		} catch (error) {
			handleError(error, "setupAssetImpressionTracking");
		}
	}

	function observeAssets() {
		var allImages = document.querySelectorAll("img");

		allImages.forEach(function (element) {
			if (element.src && !trackedAssets.has(element.src) && !isImageBlacklisted(element)) {
				log("Observing image for impression tracking: " + element.src);
				impressionObserver.observe(element);
			}
		});
	}

	/**
	 * Fallback scroll-based asset tracking for older browsers
	 */
	function setupScrollBasedAssetTracking() {
		var scrollHandler = function () {
			var allImages = document.querySelectorAll("img");

			allImages.forEach(function (element) {
				if (isElementInViewport(element) && element.src && !trackedAssets.has(element.src) && !isImageBlacklisted(element)) {
					trackAssetImpression(element);
				}
			});
		};

		// Throttle scroll events
		var scrollTimeout: number;
		window.addEventListener("scroll", function () {
			if (scrollTimeout) {
				clearTimeout(scrollTimeout);
			}
			scrollTimeout = window.setTimeout(scrollHandler, 250);
		});

		// Initial check
		scrollHandler();
	}

	function isElementInViewport(element: any) {
		const display = element.style?.display || getComputedStyle(element).display;
		if (display === "none") {
			return false;
		}

		const rect = element.getBoundingClientRect();
		const windowHeight = window.innerHeight || document.documentElement.clientHeight;
		const windowWidth = window.innerWidth || document.documentElement.clientWidth;

		// Check the central 50% of the image is visible
		const quarterHeight = rect.height * 0.25;
		const quarterWidth = rect.width * 0.25;

		return rect.top + quarterHeight >= 0 &&
			rect.left + quarterWidth >= 0 &&
			rect.bottom - quarterHeight <= windowHeight &&
			rect.right - quarterWidth <= windowWidth;
	}

	function isImageBlacklisted(imgElement: any) {
		var src = (imgElement.src || "").toLowerCase();
		var alt = (imgElement.alt || "").toLowerCase();
		var className = (imgElement.className || "").toLowerCase();
		var blacklist = CONFIG.assetImpressionSettings.blacklist;

		for (var i = 0; i < blacklist.extensions.length; i++) {
			if (src.includes(blacklist.extensions[i])) {
				return true;
			}
		}

		// Check keywords in src or alt
		var textToCheck = src + " " + alt;
		for (var j = 0; j < blacklist.keywords.length; j++) {
			if (textToCheck.includes(blacklist.keywords[j])) {
				return true;
			}
		}

		// Check CSS classes
		for (var k = 0; k < blacklist.classes.length; k++) {
			if (className.includes(blacklist.classes[k])) {
				return true;
			}
		}

		return false;
	}

	function initializeTracking() {
		log("Initializing Aprimo...");

		initializeAprimo()
			.then(function () {
				// Normal page view (Aprimo "article")
				trackArticleView(getArticleCode());

				// Conversion tracking
				setupConversionTracking();

				// Set up asset impression tracking
				setupAssetImpressionTracking();

				log("Aprimo initialized successfully");
			})
			.catch(function (error) {
				handleError(error, "initializeTracking");
			});
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", function () {
			initializeTracking();
		});
	} else {
		initializeTracking();
	}

	// Handle page visibility changes (track when user returns to tab)
	if (document.addEventListener) {
		document.addEventListener("visibilitychange", function () {
			if (!document.hidden && aprimoInitialized) {
				// User returned to the page, refresh asset tracking
				setTimeout(function () {
					if (impressionObserver) {
						observeAssets();
					}
				}, 500);
			}
		});
	}
})();